import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { PatchOrderBody } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

type UpdateOrderParams = PatchOrderBody & { id: string }

/** Applies a single-order status action via PATCH /orders/:id. */
export function useUpdateOrder() {
  const api = useOsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateOrderParams) => {
      const response = await api.patch('/orders/:id', { params: { id }, body })
      if (!response.success) throw new Error(response.error.message)
      return response.data.order
    },
    onSuccess: (order) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['product'] })
      queryClient.invalidateQueries({ queryKey: ['patient-orders', order.patientId] })
      queryClient.invalidateQueries({ queryKey: ['patient-treatments', order.patientId] })
      queryClient.invalidateQueries({ queryKey: ['patient-timeline', order.patientId] })
      if (order.consultationId)
        queryClient.invalidateQueries({ queryKey: ['consultation', order.consultationId] })
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to update order')
    },
  })
}
